/**
 * MyAccountDropdown — premium customer-account navbar entry.
 *
 * iter139 (Jul 2026) — Owner spec: mirror the simplified "Restaurants"
 * and "Partners" dropdowns. Before, the logged-out dropdown showed a
 * 500-line panel of 8 benefit tiles, 3 Learn More modals, dual CTAs
 * and 2 footer help links — visually crowded and inconsistent with
 * the sibling dropdowns. Now the logged-out state shows exactly 3
 * items:
 *
 *   1. Customer Benefits    → navigates to /for-customers (new page)
 *   2. Create Free Account  → onOpenAuth('register') (existing modal)
 *   3. Sign In              → onOpenAuth('login')    (existing modal)
 *
 * All benefit content that used to live inside this dropdown was
 * moved to `/app/frontend/src/pages/ForCustomers.jsx` — a zero-
 * rewrite migration. No backend changes, no changes to authentication,
 * customer registration, rewards, favorites, orders or account logic.
 *
 * Two states are still handled by this single component:
 *
 *   LOGGED-OUT  → 3-item dropdown (see above)
 *   LOGGED-IN   → Clicking "My Account" goes STRAIGHT to /account (the
 *                 existing dedicated customer dashboard, with its own
 *                 sidebar and sign-out control). No dropdown needed.
 *
 * Common behaviors:
 *   - Click-outside + Escape close the dropdown
 *   - Fully keyboard-accessible (role="menu")
 *   - Mobile: dropdown anchors to the right edge so it never clips
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  UserRound,
  Sparkles,
  UserPlus,
  LogIn,
} from 'lucide-react';

const ITEMS = [
  {
    key: 'customer-benefits',
    Icon: Sparkles,
    label: 'Customer Benefits',
    description: 'Pay less, on-time deliveries, rewards & more.',
    href: '/for-customers',
    testid: 'account-dropdown-item-customer-benefits',
  },
  {
    key: 'create-account',
    Icon: UserPlus,
    label: 'Create Free Account',
    description: 'Start saving in seconds. Free forever.',
    action: 'register',
    testid: 'account-dropdown-item-create-account',
  },
];

const MyAccountDropdown = ({ onOpenAuth }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const firstItemRef = useRef(null);
  const navigate = useNavigate();

  const token = typeof window !== 'undefined'
    ? localStorage.getItem('nofee_customer_token')
    : null;
  const isLoggedIn = !!token;

  // Click outside + Escape
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    setTimeout(() => firstItemRef.current?.focus(), 30);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openAuth = (mode) => {
    setOpen(false);
    onOpenAuth?.(mode);
  };

  const handleItemClick = (item) => {
    setOpen(false);
    if (item.href) {
      navigate(item.href);
    } else if (item.action) {
      onOpenAuth?.(item.action);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {isLoggedIn ? (
        // Logged-in: go STRAIGHT to /account — no dropdown needed.
        <Link
          to="/account"
          data-testid="navbar-my-account-btn"
          className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-colors text-white/85 hover:text-gold-300"
        >
          <UserRound className="w-3.5 h-3.5" />
          <span className="leading-none">{t('account_dropdown.my_account', 'My Account')}</span>
        </Link>
      ) : (
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          data-testid="navbar-my-account-btn"
          className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-colors text-white/85 hover:text-gold-300"
        >
          <UserRound className="w-3.5 h-3.5" />
          <span className="leading-none">{t('account_dropdown.my_account', 'My Account')}</span>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      )}

      {open && !isLoggedIn && (
        <div
          role="menu"
          aria-label={t('account_dropdown.menu_aria', 'My account menu')}
          data-testid="account-dropdown-menu"
          data-state="logged-out"
          className="absolute right-0 mt-2 w-[380px] max-w-[calc(100vw-1.5rem)] origin-top-right rounded-2xl border border-white/[0.08] bg-gradient-to-b from-zinc-950 to-black shadow-[0_24px_60px_-20px_rgba(0,0,0,0.7)] p-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150"
        >
          {/* Section 1 — For New Customers */}
          <div className="px-3 pt-3 pb-2">
            <p className="text-[10.5px] uppercase tracking-[0.22em] text-gold-400 font-bold">
              For New Customers
            </p>
            <p className="text-[12px] text-white/45 mt-0.5">
              Everything a NoFee customer gets — in one place.
            </p>
          </div>
          <div className="h-px bg-white/[0.06] mx-2" />

          <div className="py-1">
            {ITEMS.map((it, idx) => {
              const { Icon } = it;
              return (
                <button
                  key={it.key}
                  ref={idx === 0 ? firstItemRef : null}
                  type="button"
                  role="menuitem"
                  onClick={() => handleItemClick(it)}
                  data-testid={it.testid}
                  className="w-full text-left flex items-start gap-3 px-3 py-3 rounded-xl hover:bg-white/[0.04] focus:bg-white/[0.05] outline-none transition-colors group"
                >
                  <span className="w-9 h-9 rounded-xl bg-gold-500/10 border border-gold-500/20 flex items-center justify-center flex-shrink-0 group-hover:border-gold-500/40 transition-colors">
                    <Icon className="w-4 h-4 text-gold-300" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[14px] font-semibold text-white">
                      {it.label}
                    </span>
                    <span className="block text-[12px] text-white/55 leading-snug mt-0.5">
                      {it.description}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* Section 2 — Already have a NoFee account? */}
          <div className="px-3 pt-3 pb-3 border-t border-white/[0.06] mt-1">
            <p className="text-[10.5px] uppercase tracking-[0.22em] text-gold-400 font-bold mb-2">
              Already have a NoFee account?
            </p>
            <button
              type="button"
              onClick={() => openAuth('login')}
              data-testid="account-dropdown-item-signin"
              className="group w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[13.5px] tracking-tight transition-all shadow-[0_12px_32px_-12px_rgba(250,204,21,0.55)] hover:-translate-y-0.5"
            >
              <LogIn className="w-4 h-4" />
              Sign In
            </button>
            <p className="text-[11.5px] text-white/45 text-center mt-2">
              Access your rewards, orders and favorites.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAccountDropdown;
